import React, { useEffect } from 'react';
import './ItemInfoModal.css';

/**
 * ItemInfoModal - MapleStory-style item information display
 *
 * Full-screen modal showing comprehensive item details with beautiful formatting
 */
const ItemInfoModal = ({ item, onClose }) => {
  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!item) return null;

  // Rarity color mapping
  const rarityColors = {
    common: '#9ca3af',
    uncommon: '#10b981',
    rare: '#3b82f6',
    epic: '#a855f7',
    legendary: '#f59e0b',
  };

  const rarityColor = rarityColors[item.rarity] || rarityColors.common;

  return (
    <div className="item-info-modal-overlay" onClick={onClose}>
      <div className="item-info-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="item-info-close" onClick={onClose} title="Close (ESC)">
          ×
        </button>

        {/* Header */}
        <div className="item-info-header" style={{ borderColor: rarityColor }}>
          <div className="item-info-icon-large" style={{ filter: `drop-shadow(0 0 12px ${rarityColor})` }}>
            {item.icon}
          </div>
          <div className="item-info-title-section">
            <h2 className="item-info-name" style={{ color: rarityColor }}>
              {item.name}
            </h2>
            <div className="item-info-rarity" style={{ color: rarityColor }}>
              {item.rarity.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="item-info-content">
          {/* Basic Info Section */}
          <div className="item-info-section">
            <h3 className="item-info-section-title">Basic Information</h3>
            <div className="item-info-grid">
              {item.slot && (
                <div className="item-info-field">
                  <span className="item-info-label">Equipment Slot:</span>
                  <span className="item-info-value">{formatSlotName(item.slot)}</span>
                </div>
              )}
              {item.category && (
                <div className="item-info-field">
                  <span className="item-info-label">Category:</span>
                  <span className="item-info-value">{formatCategoryName(item.category)}</span>
                </div>
              )}
              {item.stackSize && item.stackSize > 1 && (
                <div className="item-info-field">
                  <span className="item-info-label">Stack Size:</span>
                  <span className="item-info-value">{item.stackSize}</span>
                </div>
              )}
              {item.sellValue !== undefined && (
                <div className="item-info-field">
                  <span className="item-info-label">Sell Value:</span>
                  <span className="item-info-value gold">💰 {item.sellValue}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {item.description && (
            <div className="item-info-section">
              <h3 className="item-info-section-title">Description</h3>
              <div className="item-info-description">
                <em>"{item.description}"</em>
              </div>
            </div>
          )}

          {/* Stats Section (Equipment) */}
          {item.stats && Object.keys(item.stats).length > 0 && (
            <div className="item-info-section">
              <h3 className="item-info-section-title">Statistics</h3>
              <div className="item-info-stats-grid">
                {Object.entries(item.stats).map(([stat, value]) => (
                  <div key={stat} className="item-info-stat-card">
                    <div className="item-info-stat-icon">{getStatIcon(stat)}</div>
                    <div className="item-info-stat-details">
                      <div className="item-info-stat-name">{formatStatName(stat)}</div>
                      <div className="item-info-stat-value positive">+{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Effect Section (Consumables/Buffs) */}
          {item.effect && (
            <div className="item-info-section">
              <h3 className="item-info-section-title">Effect</h3>
              <div className="item-info-effect-box">
                {item.effect.type === 'heal' && (
                  <div className="item-info-effect-content">
                    <div className="item-info-effect-icon">❤️</div>
                    <div className="item-info-effect-details">
                      <div className="item-info-effect-text">
                        Restores <strong className="positive">{item.effect.value}</strong> HP
                      </div>
                    </div>
                  </div>
                )}
                {item.effect.type === 'buff' && (
                  <div className="item-info-effect-content">
                    <div className="item-info-effect-icon">✨</div>
                    <div className="item-info-effect-details">
                      {item.effect.stat && (
                        <div className="item-info-effect-text">
                          <strong className="positive">+{Math.round(item.effect.value * 100)}%</strong> {formatStatName(item.effect.stat)}
                        </div>
                      )}
                      {item.effect.stats && Object.entries(item.effect.stats).map(([stat, val]) => (
                        <div key={stat} className="item-info-effect-text">
                          <strong className="positive">+{Math.round(val * 100)}%</strong> {formatStatName(stat)}
                        </div>
                      ))}
                      {item.effect.duration && (
                        <div className="item-info-effect-duration">
                          Duration: {formatDuration(item.effect.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {item.effect.type === 'reveal_treasure' && (
                  <div className="item-info-effect-content">
                    <div className="item-info-effect-icon">🗺️</div>
                    <div className="item-info-effect-details">
                      <div className="item-info-effect-text">
                        Reveals a hidden treasure location on the world map
                      </div>
                    </div>
                  </div>
                )}
                {item.effect.type === 'teleport_entrance' && (
                  <div className="item-info-effect-content">
                    <div className="item-info-effect-icon">🌀</div>
                    <div className="item-info-effect-details">
                      <div className="item-info-effect-text">
                        Instantly teleports you back to the dungeon entrance
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Requirements Section */}
          {item.requirements && (
            <div className="item-info-section">
              <h3 className="item-info-section-title">Requirements</h3>
              <div className="item-info-requirements">
                {item.requirements.level && (
                  <div className="item-info-requirement-badge">
                    <span className="requirement-icon">📊</span>
                    Level {item.requirements.level}+
                  </div>
                )}
                {item.requirements.class && (
                  <div className="item-info-requirement-badge">
                    <span className="requirement-icon">⚔️</span>
                    Class: {item.requirements.class}
                  </div>
                )}
                {item.requirements.quest && (
                  <div className="item-info-requirement-badge">
                    <span className="requirement-icon">📜</span>
                    Quest: {item.requirements.quest}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quest Item Warning */}
          {item.category === 'quest' && (
            <div className="item-info-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">Quest Item - Cannot be sold or traded</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="item-info-footer">
          <button className="item-info-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function formatSlotName(slot) {
  const slotNames = {
    weapon: 'Weapon',
    offhand: 'Off-hand',
    helmet: 'Helmet',
    top: 'Top',
    bottom: 'Bottom',
    gloves: 'Gloves',
    boots: 'Boots',
    cape: 'Cape',
    belt: 'Belt',
    pendant1: 'Pendant',
    pendant2: 'Pendant',
    ring1: 'Ring',
    ring2: 'Ring',
  };
  return slotNames[slot] || slot;
}

function formatCategoryName(category) {
  const categoryNames = {
    potion: 'Consumable',
    material: 'Crafting Material',
    buff: 'Buff Item',
    quest: 'Quest Item',
    misc: 'Miscellaneous',
  };
  return categoryNames[category] || category;
}

function formatStatName(stat) {
  const statNames = {
    health: 'Max HP',
    attack: 'Attack',
    defense: 'Defense',
    speed: 'Speed',
    critChance: 'Crit Rate',
    critDamage: 'Crit Damage',
    range: 'Range',
  };
  return statNames[stat] || stat;
}

function getStatIcon(stat) {
  const statIcons = {
    health: '❤️',
    attack: '⚔️',
    defense: '🛡️',
    speed: '⚡',
    critChance: '💥',
    critDamage: '💢',
    range: '🎯',
  };
  return statIcons[stat] || '📊';
}

function formatDuration(seconds) {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `${seconds} seconds`;
}

export default ItemInfoModal;
